import React, { useState, useEffect } from "react";
import { Form } from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import AccountSectionWrapper from "./AccountSectionWrapper";
import ModalNew from "../popup/Modal";
import ApiResponseModal from "../popup/ApiResponseModal";
import { PopupHeader, PopupContent } from "../popup/common";
import LoadingPlaceholder from "../loading/LoadingPlaceholder";
import AvatarUploadModal from "../forms/AvatarUploadModal";
import DateInput from "../forms/DateInput";
import {
  useUpdateAccountSettingsMutation,
  useGetAccountQuery,
  accountApi,
} from "../../../api/account";
import { updateSettingsAvatar } from "../../../api/account/accountAPI";
import { useDispatch } from "react-redux";
import { usaStates } from "../forms/selectOptionData";
import { formatDateForDisplay } from "../../../utils/date/DateUtils";
import { formatPhoneNumber, formatPhoneInput, cleanPhoneNumber } from "../../../utils/formatting/phoneFormatter";

const PersonalContent = () => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null); // 'personal' or 'address'
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Function to convert state code to state name
  const getStateName = (stateCode) => {
    if (!stateCode) return stateCode;
    const state = usaStates().find((s) => s.value === stateCode.toLowerCase());
    return state ? state.label : stateCode;
  };

  // API hooks
  const [updateAccountSettings, { isError, error }] =
    useUpdateAccountSettingsMutation();
  const { data: accountInfo, isLoading, isSuccess } = useGetAccountQuery();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    dateOfBirth: "",
    aboutMe: "",
  });
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [isAvatarResultModalOpen, setIsAvatarResultModalOpen] = useState(false);
  const [avatarResultMessage, setAvatarResultMessage] = useState("");
  const [isAvatarSuccess, setIsAvatarSuccess] = useState(false);
  const [isAvatarResultLoading, setIsAvatarResultLoading] = useState(false);

  // Personal info result modal states
  const [isPersonalResultModalOpen, setIsPersonalResultModalOpen] =
    useState(false);
  const [personalResultMessage, setPersonalResultMessage] = useState("");
  const [isPersonalSuccess, setIsPersonalSuccess] = useState(false);
  const [isPersonalResultLoading, setIsPersonalResultLoading] = useState(false);

  const [addressData, setAddressData] = useState({
    street: "",
    line2: "",
    apt: "",
    city: "",
    state: "",
    country: "",
    zipCode: "",
  });

  // Load data from API
  useEffect(() => {
    if (isSuccess && accountInfo) {
      // Load personal info
      if (accountInfo.personal_info && !isEmpty(accountInfo.personal_info)) {
        setFormData({
          firstName: accountInfo.personal_info.first_name || "",
          lastName: accountInfo.personal_info.last_name || "",
          phoneNumber: accountInfo.personal_info.phone_number || "",
          dateOfBirth: accountInfo.personal_info.date_of_birth || "",
          aboutMe: accountInfo.personal_info.about_me || "",
        });
      }

      // Load avatar
      if (accountInfo.personal_info && accountInfo.personal_info.avatar) {
        // Use avatar URL directly like ArticlePrivate does
        setAvatarUrl(accountInfo.personal_info.avatar);
      }

      // Load address info
      if (accountInfo.addresses && accountInfo.addresses[0]) {
        const address = accountInfo.addresses[0];
        setAddressData({
          street: address.address_line_one || "",
          line2: address.address_line_two || "",
          apt: address.address_apt || "",
          city: address.address_city || "",
          state: address.address_state || "",
          country: address.address_country || "",
          zipCode: address.address_zip || "",
        });
      }
    }
  }, [accountInfo, isSuccess]);

  const handleEditClick = () => {
    setModalType("personal");
    setIsModalOpen(true);
  };

  const handleAddressEditClick = () => {
    setModalType("address");
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalType(null);
  };

  const handleSaveChanges = async (event) => {
    event.preventDefault();

    // Prepare changes
    let changes = null;
    if (modalType === "personal") {
      changes = {
        type: "personal",
        setting: "PersonalInfo",
        values: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone_number: cleanPhoneNumber(formData.phoneNumber),
          date_of_birth: formData.dateOfBirth,
          about_me: formData.aboutMe,
        },
        displayData: formData,
      };
    } else if (modalType === "address") {
      changes = {
        type: "address",
        setting: "Address",
        values: {
          address_line_one: addressData.street,
          address_line_two: addressData.line2,
          address_apt: addressData.apt,
          address_city: addressData.city,
          address_state: addressData.state,
          address_country: "United States",
          address_zip: addressData.zipCode,
        },
        displayData: addressData,
      };
    }

    // Close edit modal and show result modal with loading
    setIsModalOpen(false);
    setIsPersonalResultModalOpen(true);
    setIsPersonalResultLoading(true);

    try {
      const result = await updateAccountSettings({
        setting: changes.setting,
        values: changes.values,
      });

      // Add minimum delay to show loading placeholder
      // await new Promise(resolve => setTimeout(resolve, 800));

      if (result.error) {
        console.error("Account settings error: ", result.error);
        setIsPersonalSuccess(false);
        setPersonalResultMessage(
          "We couldn't update your information. Please try again."
        );
      } else {
        setIsPersonalSuccess(true);
        setPersonalResultMessage(
          "Your information has been updated successfully."
        );
      }

      setIsPersonalResultLoading(false);
      setModalType(null);
    } catch (error) {
      console.error("Error saving account settings:", error);
      setIsPersonalSuccess(false);
      setPersonalResultMessage(
        "Failed to update information. Please try again."
      );
      setIsPersonalResultLoading(false);
    }
  };


  const handleCloseAvatarResultModal = () => {
    setIsAvatarResultModalOpen(false);
    setAvatarResultMessage("");
    setIsAvatarSuccess(false);
    setIsAvatarResultLoading(false);
  };

  const handleRetryAvatar = () => {
    setIsAvatarResultModalOpen(false);
    setIsAvatarModalOpen(true);
  };

  const handleClosePersonalResultModal = () => {
    setIsPersonalResultModalOpen(false);
    setPersonalResultMessage("");
    setIsPersonalSuccess(false);
    setIsPersonalResultLoading(false);
  };

  const handleRetryPersonal = () => {
    setIsPersonalResultModalOpen(false);
    setIsModalOpen(true);
  };

  const handleInputChange = (field, value) => {
    if (modalType === "personal") {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    } else if (modalType === "address") {
      setAddressData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  // Show loading state while data is being fetched
  if (isLoading) {
    return <LoadingPlaceholder rows={2} type="card-large" />;
  }

  return (
    <>
      <AccountSectionWrapper
        title="Personal Information"
        buttonText="Edit"
        onButtonClick={handleEditClick}
      >
        {/* Personal information content */}
        <div className="personal-info-container">
          {/* First Column */}
          <div className="personal-info-left">
            {/* Profile Image Section */}
            <div className="profile-image-section">
              <div className="profile-image">
                <img
                  src={
                    avatarUrl ||
                    `${process.env.PUBLIC_URL}/images/avatar_settings.svg`
                  }
                  alt="Profile Avatar"
                />
              </div>
              <button
                className="update-image-link"
                onClick={() => setIsAvatarModalOpen(true)}
              >
                <img
                  src="/images/icons/upload_blue.svg"
                  alt="Upload"
                  className="upload-icon"
                />
                Update Image
              </button>
            </div>

            {/* About Me Section */}
            <div className="form-field">
              <label className="field-label">About me</label>
              <div className="form-textarea-representation">
                <p>{formData.aboutMe || "We'd love to know more about you!"}</p>
              </div>
            </div>
          </div>

          {/* Second Column */}
          <div className="personal-info-right">
            {/* First Name */}
            <div className="form-field">
              <label className="field-label">First Name</label>
              <div className="form-input-representation">
                <p>{formData.firstName}</p>
              </div>
            </div>

            {/* Last Name */}
            <div className="form-field">
              <label className="field-label">Last Name</label>
              <div className="form-input-representation">
                <p>{formData.lastName}</p>
              </div>
            </div>

            {/* Phone Number */}
            <div className="form-field">
              <label className="field-label">Phone Number</label>
              <div className="form-input-representation">
                <p>{formatPhoneNumber(formData.phoneNumber)}</p>
              </div>
            </div>

            {/* Date of Birth */}
            <div className="form-field">
              <label className="field-label">Date of Birth</label>
              <div className="form-input-representation">
                <p>{formatDateForDisplay(formData.dateOfBirth)}</p>
              </div>
            </div>
          </div>
        </div>
      </AccountSectionWrapper>

      <AccountSectionWrapper
        title="Personal Address"
        buttonText="Edit"
        onButtonClick={handleAddressEditClick}
      >
        {/* Personal address content */}
        <div className="address-content">
          {/* Street */}
          <div className="form-field">
            <label className="field-label">Street</label>
            <div className="form-input-representation">
              <p>{addressData.street}</p>
            </div>
          </div>

          {/* Line 2 */}
          <div className="form-field">
            <label className="field-label">Line 2</label>
            <div className="form-input-representation">
              <p>{addressData.line2}</p>
            </div>
          </div>

          {/* Apt # and City in one line */}
          <div className="address-row">
            <div className="form-field address-field-half">
              <label className="field-label">Apt #</label>
              <div className="form-input-representation">
                <p>{addressData.apt}</p>
              </div>
            </div>
            <div className="form-field address-field-half">
              <label className="field-label">City</label>
              <div className="form-input-representation">
                <p>{addressData.city}</p>
              </div>
            </div>
          </div>

          {/* State, Country and ZIP in one line */}
          <div className="address-row">
            <div className="form-field address-field-third">
              <label className="field-label">State</label>
              <div className="form-input-representation">
                <p>{getStateName(addressData.state)}</p>
              </div>
            </div>
            <div className="form-field address-field-third">
              <label className="field-label">Country</label>
              <div className="form-input-representation">
                <p>United States</p>
              </div>
            </div>
            <div className="form-field address-field-third">
              <label className="field-label">ZIP Code</label>
              <div className="form-input-representation">
                <p>{addressData.zipCode}</p>
              </div>
            </div>
          </div>
        </div>
      </AccountSectionWrapper>

      {/* Adaptive Modal */}
      {isModalOpen && (
        <ModalNew className="content-modal" style={{ display: isDatePickerOpen ? "none" : undefined }}>
          <PopupHeader
            title={
              modalType === "personal"
                ? "Personal Information"
                : "Personal Address"
            }
            setOpenState={setIsModalOpen}
            icon={`${process.env.PUBLIC_URL}/images/icons/${modalType === "personal" ? "user_blue_popup.svg" : "home_blue_popup.svg"}`}
            variant="compact"
          />
          
          <PopupContent>
            <Form className="modal-form" onSubmit={handleSaveChanges}>
            {modalType === "personal" && (
              <>
                <div className="form-field">
                  <label className="field-label">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-field">
                  <label className="field-label">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-field">
                  <label className="field-label">Phone Number</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formatPhoneNumber(formData.phoneNumber)}
                    onChange={(e) =>
                      handleInputChange("phoneNumber", formatPhoneInput(e.target.value))
                    }
                    className="form-input"
                    placeholder="(###)-###-####"
                  />
                </div>

                <div className="form-field">
                  <label className="field-label">Date of Birth</label>
                  <DateInput
                    name="dateOfBirth"
                    label="Date of Birth"
                    value={formData.dateOfBirth}
                    onOpen={() => setIsDatePickerOpen(true)}
                    onClose={() => setIsDatePickerOpen(false)}
                    onChange={(e) =>
                      handleInputChange("dateOfBirth", e.target.value)
                    }
                  />
                </div>

                <div className="form-field">
                  <label className="field-label">About me</label>
                  <textarea
                    name="aboutMe"
                    value={formData.aboutMe}
                    onChange={(e) =>
                      handleInputChange("aboutMe", e.target.value)
                    }
                    className="form-textarea"
                    rows={4}
                  />
                </div>
              </>
            )}

            {modalType === "address" && (
              <>
                {/* Street */}
                <div className="form-field">
                  <label className="field-label">Street</label>
                  <input
                    type="text"
                    name="street"
                    value={addressData.street}
                    onChange={(e) =>
                      handleInputChange("street", e.target.value)
                    }
                    className="form-input"
                    required
                  />
                </div>

                {/* Line 2 */}
                <div className="form-field">
                  <label className="field-label">Line 2</label>
                  <input
                    type="text"
                    name="line2"
                    value={addressData.line2}
                    onChange={(e) => handleInputChange("line2", e.target.value)}
                    className="form-input"
                  />
                </div>

                {/* Apt # and City in one line */}
                <div className="address-row">
                  <div className="form-field address-field-half">
                    <label className="field-label">Apt #</label>
                    <input
                      type="text"
                      name="apt"
                      value={addressData.apt}
                      onChange={(e) => handleInputChange("apt", e.target.value)}
                      className="form-input"
                    />
                  </div>
                  <div className="form-field address-field-half">
                    <label className="field-label">City</label>
                    <input
                      type="text"
                      name="city"
                      value={addressData.city}
                      onChange={(e) =>
                        handleInputChange("city", e.target.value)
                      }
                      className="form-input"
                      required
                    />
                  </div>
                </div>

                {/* State, Country and ZIP in one line */}
                <div className="address-row">
                  <div className="form-field address-field-third">
                    <label className="field-label">State</label>
                    <select
                      name="state"
                      value={addressData.state}
                      onChange={(e) =>
                        handleInputChange("state", e.target.value)
                      }
                      className="form-input state-select"
                      required
                    >
                      <option value="">Select State</option>
                      {usaStates().map((state) => (
                        <option key={state.value} value={state.value}>
                          {state.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field address-field-third">
                    <label className="field-label">Country</label>
                    <div className="form-input-representation">
                      <p>United States</p>
                    </div>
                  </div>
                  <div className="form-field address-field-third">
                    <label className="field-label">ZIP Code</label>
                    <input
                      type="text"
                      name="zipCode"
                      value={addressData.zipCode}
                      onChange={(e) =>
                        handleInputChange("zipCode", e.target.value)
                      }
                      className="form-input"
                      required
                    />
                  </div>
                </div>
              </>
            )}

            <div className="modal-actions">
              <button
                type="submit"
                className="cta-button primary text-center"
              >
                Save Changes
              </button>
            </div>
          </Form>
          </PopupContent>
        </ModalNew>
      )}

      {/* Avatar Upload Modal */}
      <AvatarUploadModal
        isOpen={isAvatarModalOpen}
        onClose={() => setIsAvatarModalOpen(false)}
        avatarUrl={avatarUrl}
        uploadFunction={updateSettingsAvatar}
        onUploadSuccess={(newAvatarUrl) => {
          setAvatarUrl(newAvatarUrl);
          setIsAvatarModalOpen(false);
          setIsAvatarResultModalOpen(true);
          setIsAvatarResultLoading(true);
          
          // Invalidate RTK Query cache to update all components
          dispatch(accountApi.util.invalidateTags(["account-info"]));
          
          setIsAvatarSuccess(true);
          setAvatarResultMessage(
            "Your information has been updated successfully."
          );
          setIsAvatarResultLoading(false);
        }}
        onUploadError={(errorMessage) => {
          setIsAvatarModalOpen(false);
          setIsAvatarResultModalOpen(true);
          setIsAvatarResultLoading(false);
          setIsAvatarSuccess(false);
          setAvatarResultMessage(errorMessage);
        }}
      />

      {/* Avatar Result Modal */}
      <ApiResponseModal
        isOpen={isAvatarResultModalOpen}
        isLoading={isAvatarResultLoading}
        isSuccess={isAvatarSuccess}
        message={avatarResultMessage}
        onClose={handleCloseAvatarResultModal}
        onRetry={handleRetryAvatar}
      />

      {/* Personal Info Result Modal */}
      <ApiResponseModal
        isOpen={isPersonalResultModalOpen}
        isLoading={isPersonalResultLoading}
        isSuccess={isPersonalSuccess}
        message={personalResultMessage}
        onClose={handleClosePersonalResultModal}
        onRetry={handleRetryPersonal}
      />
    </>
  );
};

export default PersonalContent;
